/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import { Box, CircularProgress, Stack } from "@mui/material";

import { Nullable, User } from "../../utils/Interfaces";
import AssigneesList from "./AssigneesList";
import AssigneeTicketList from "./AssigneeTicketList";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import Grid from "@mui/material/Grid";
import { useActiveAssigneesList } from "./assignmentsHooks";

const AssignmentsManager = () => {
    const [selectedUser, setSelectedUser] = useState<Nullable<User>>(null);

    function activeAssignmentCount(user: User): string {
        type UserWithAssignmentCount = User & { assignments_count: number };
        let asg_count: number = (user as UserWithAssignmentCount).assignments_count;
        return `${asg_count} Active Assignment${asg_count === 1 ? "" : "s"}`;
    }

    function sortUsersAlphabetically(users: User[]): User[] {
        return users.sort((a, b) =>
            (a.first_name + a.last_name).toLowerCase() > (b.first_name + b.last_name).toLowerCase() ? 1 : -1
        );
    }

    const { data, status } = useActiveAssigneesList();

    if (!selectedUser && (data?.results.length ?? []) > 0) {
        setSelectedUser(data?.results[0] ?? null);
    }

    if (status === "pending") {
        return (
            <Box>
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        { text: "Service", route: "/?section=office" },
                        { text: "Assignments Manager", route: "/assignments" },
                    ]}
                />

                <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
                    <CircularProgress size={40} sx={{ mt: 20 }} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    { text: "Assignments Manager", route: "/assignments" },
                ]}
            />
            <Stack direction="row" spacing={2}>
                <Grid container xs={12}>
                    <Grid item xs={2.5}>
                        <AssigneesList
                            assignees={sortUsersAlphabetically(data?.results ?? [])}
                            setSelectedUser={setSelectedUser}
                            subtextFunction={activeAssignmentCount}
                        />
                    </Grid>
                    <Grid item xs={9.5}>
                        <AssigneeTicketList userId={selectedUser?.id ?? null} />
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
};

export default AssignmentsManager;
